/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

import {
  useNavigate,
  useParams,
  NavigateOptions,
  useLocation,
} from "react-router-dom";
import type { To } from "react-router-dom";
import { useCallback } from "react";

/**
 * Custom hook for event-scoped routing
 *
 * This hook wraps React Router's navigation to automatically include
 * the eventId parameter in routes when navigating within event-scoped pages.
 *
 * Routes that don't need eventId (e.g., login, unsubscribe, register-link)
 * will work without modification.
 */
const useEventRouting = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { eventId: paramEventId } = useParams<{ eventId: string }>();

  // Use eventId from params if available, otherwise try to derive it from the first path segment.
  // This is useful for components rendered outside of the Route tree (like Providers).
  const eventId =
    paramEventId ||
    (() => {
      const segments = pathname.split("/").filter(Boolean);
      const firstSegment = segments[0];
      const reservedPaths = [
        "register-link",
        "login",
        "select-event",
        "no-access",
        "forgot-password",
        "create-new-password",
      ];
      if (firstSegment && !reservedPaths.includes(firstSegment)) {
        return firstSegment;
      }
      return undefined;
    })();

  /**
   * Prepends eventId to a given path if eventId exists
   *
   * @param path - The route path to navigate to
   * @returns The path with eventId prepended if eventId exists, otherwise the original path
   *
   * @example
   * // If eventId = "abc123"
   * getEventRoute("/dashboard") // Returns "/abc123/dashboard"
   * getEventRoute(ROUTES.dashboard.base) // Returns "/abc123/dashboard"
   *
   * // If no eventId (e.g., on login page)
   * getEventRoute("/dashboard") // Returns "/dashboard"
   */
  const getEventRoute = useCallback(
    (path: To): To => {
      // If path is an object (Partial<Path>), return it as-is
      // We only transform string paths
      if (typeof path !== "string") {
        return path;
      }

      // Don't modify the path if:
      // 1. There's no eventId (we're on a non-event route like login)
      // 2. The path is relative navigation (e.g., "..", ".", or starts with "../")
      // 3. The path is external (starts with http:// or https://)
      // 4. The path is already event-scoped (starts with /eventId or /eventId/)
      if (
        !eventId ||
        path === ".." ||
        path === "." ||
        path.startsWith("../") ||
        path.startsWith("http://") ||
        path.startsWith("https://") ||
        (eventId &&
          (path === `/${eventId}` || path.startsWith(`/${eventId}/`)))
      ) {
        return path;
      }

      // Remove leading slash from path if it exists to avoid double slashes
      const cleanPath = path.startsWith("/") ? path.slice(1) : path;

      // Prepend eventId to the path
      return `/${eventId}/${cleanPath}`;
    },
    [eventId],
  );

  /**
   * Navigate function that automatically includes eventId in the path
   *
   * @param to - The path to navigate to (string or number for history navigation)
   * @param options - Optional navigation options from React Router
   *
   * @example
   * navigateWithEvent(ROUTES.dashboard.base)
   * navigateWithEvent(ROUTES.dashboard.guest(userId))
   * navigateWithEvent(-1) // Go back (relative navigation)
   * navigateWithEvent("..", { replace: true }) // Go up one level with replace
   */
  const navigateWithEvent = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      // If navigating with a number (e.g., -1 for back, 1 for forward), pass through directly
      if (typeof to === "number") {
        navigate(to);
        return;
      }

      // For string paths, prepend eventId if needed
      const eventPath = getEventRoute(to as To);
      navigate(eventPath, options);
    },
    [navigate, getEventRoute],
  );

  /**
   * Checks if the current page matches the given route, handling event scoping
   *
   * @param route - The route to check against (e.g., ROUTES.login)
   * @param options - Options for matching (exact: boolean, default true)
   * @returns true if the current pathname matches the route
   */
  const isCurrentPage = useCallback(
    (route: string, { exact = true } = {}) => {
      const eventRoute = getEventRoute(route) as string;
      const cleanPath =
        pathname.endsWith("/") && pathname.length > 1
          ? pathname.slice(0, -1)
          : pathname;
      const cleanEventRoute =
        eventRoute.endsWith("/") && eventRoute.length > 1
          ? eventRoute.slice(0, -1)
          : eventRoute;

      if (exact) {
        return cleanPath === cleanEventRoute;
      }
      return cleanPath.startsWith(cleanEventRoute);
    },
    [pathname, getEventRoute],
  );

  return {
    navigateWithEvent,
    getEventRoute,
    isCurrentPage,
    pathname,
    eventId,
  };
};

export default useEventRouting;
